import { getSupabaseOrThrow } from '../supabaseClient'

export type AdminProfile = {
  id: string
  full_name: string | null
  origin: string | null
  created_at: string
  updated_at: string
}

export type AdminPlaceComment = {
  id: string
  place_id: string
  user_id: string
  comment: string
  created_at: string
  updated_at: string
}

export type AdminWaitlistSignup = {
  id: string | number
  email: string
  source: string | null
  created_at: string
}

/** Konto z promocji /promocja (pełny dostęp, wpis w free_promo_grants). */
export type AdminFreePromoAccountRow = {
  grant_id: string
  user_id: string
  email: string
  full_name: string | null
  origin: string | null
  granted_at: string
  registered_at: string | null
  marketing_consent: boolean
  marketing_consent_at: string | null
}

/** Konto testowe (podgląd listy miejsc, bez wpisowego). */
export type AdminTrialAccountRow = {
  id: string
  email: string | null
  full_name: string | null
  origin: string | null
  created_at: string
  membership_paid_at: string | null
  marketing_consent: boolean
  marketing_consent_at: string | null
}

/** Połączenie konta Auth + opcjonalnego profilu (public.profiles). */
export type AdminUserRow = {
  id: string
  email: string | null
  full_name: string | null
  origin: string | null
  registered_at: string
  marketing_consent: boolean
  marketing_consent_at: string | null
}

export type AdminUserJournalEntry = {
  place_id: string
  visited_at: string | null
  verified_at: string | null
  verification_method: string | null
  note: string | null
  photo_url: string | null
  has_photo: boolean
  photo_visibility: 'private' | 'public'
  updated_at: string | null
  place: {
    id: string
    name: string | null
    mock_id: number | null
  } | null
}

export type AdminUserJournalPhoto = {
  id: string
  user_id: string
  place_id: string
  url: string
  is_storage_url: boolean
  visibility: 'private' | 'public'
  created_at: string
  place: {
    id: string
    name: string | null
    mock_id: number | null
  } | null
}

export type AdminHotpayPaidSummary = {
  payment_count: number
  amount_sum: number
}

export type AdminHotpayEventRow = {
  id: number
  id_platnosci: string
  id_zamowienia: string
  notify_status: string
  kwota: string | null
  created_at: string
  registration_checkout_id: string | null
  auth_user_id: string | null
}

export async function isCurrentUserAdmin(): Promise<boolean> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('is_admin')
  if (error) throw new Error(error.message)
  return Boolean(data)
}

function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

export async function getHotpayPaidSummary(input: { since: Date | null }): Promise<AdminHotpayPaidSummary> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_hotpay_paid_summary', {
    p_since: input.since ? input.since.toISOString() : null,
  })
  if (error) throw new Error(error.message)

  const row = (Array.isArray(data) ? data[0] : data) as unknown as { payment_count?: unknown; amount_sum?: unknown } | null
  return {
    payment_count: Math.floor(toNumber(row?.payment_count, 0)),
    amount_sum: toNumber(row?.amount_sum, 0),
  }
}

export async function listHotpayEvents(input: {
  since: Date | null
  limit?: number
  offset?: number
  status?: string | null
}): Promise<{ rows: AdminHotpayEventRow[]; total: number | null }> {
  const supabase = getSupabaseOrThrow()
  // Kolejność kluczy: zgodna z oczekiwaną sygnaturą PostgREST (alfabetycznie po nazwach parametrów).
  const { data, error } = await supabase.rpc('admin_list_hotpay_events', {
    p_limit: input.limit ?? 200,
    p_offset: input.offset ?? 0,
    p_since: input.since ? input.since.toISOString() : null,
    p_status: input.status ?? null,
  })
  if (error) throw new Error(error.message)

  const payload = data as unknown as { total?: unknown; rows?: unknown } | Array<unknown> | null

  // RPC może zwracać { total, rows } albo (w starszej wersji) bezpośrednio tablicę wierszy.
  if (Array.isArray(payload)) {
    return { rows: payload as AdminHotpayEventRow[], total: null }
  }

  const total = typeof payload?.total === 'number' && Number.isFinite(payload.total) ? payload.total : null
  const rows = (Array.isArray(payload?.rows) ? payload?.rows : []) as AdminHotpayEventRow[]
  return { rows, total }
}

export async function listProfiles(limit = 500): Promise<AdminProfile[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, origin, created_at, updated_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)
  return (data ?? []) as AdminProfile[]
}

type ProfileRow = {
  id: string
  full_name: string | null
  origin: string | null
  created_at: string
  marketing_consent: boolean | null
  marketing_consent_at: string | null
}

const ADMIN_USERS_PAGE = 1000
const ADMIN_USERS_MAX = 100_000
const PROFILE_IDS_CHUNK = 200

async function fetchProfilesByUserIds(userIds: string[]): Promise<Map<string, ProfileRow>> {
  const supabase = getSupabaseOrThrow()
  const map = new Map<string, ProfileRow>()
  const ids = [...new Set(userIds)].filter(Boolean)
  for (let i = 0; i < ids.length; i += PROFILE_IDS_CHUNK) {
    const chunk = ids.slice(i, i + PROFILE_IDS_CHUNK)
    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, origin, created_at, marketing_consent, marketing_consent_at')
      .in('id', chunk)
    if (error) throw new Error(error.message)
    for (const p of (data ?? []) as ProfileRow[]) {
      map.set(p.id, p)
    }
  }
  return map
}

async function profilesFallback(limit: number): Promise<{ rows: AdminUserRow[]; source: 'profiles_fallback' }> {
  const supabase = getSupabaseOrThrow()
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('id, full_name, origin, created_at, marketing_consent, marketing_consent_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (profileError) throw new Error(profileError.message)
  const profiles = (profileData ?? []) as ProfileRow[]
  return {
    source: 'profiles_fallback',
    rows: profiles.map((p) => ({
      id: p.id,
      email: null,
      full_name: p.full_name,
      origin: p.origin,
      registered_at: p.created_at,
      marketing_consent: p.marketing_consent === true,
      marketing_consent_at: p.marketing_consent_at ?? null,
    })),
  }
}

/**
 * Wszyscy użytkownicy z Auth (RPC `admin_list_users` w Supabase) + uzupełnienie z `profiles`.
 * Jeśli RPC nie jest wdrożone / zwraca błąd przy pierwszej stronie, zwraca wyłącznie wiersze z `profiles`
 * (wtedy mogą brakować kont bez profilu , np. świeża rejestracja przed pierwszym zapisem profilu).
 */
export async function listAdminUsers(limit = 5000): Promise<{ rows: AdminUserRow[]; source: 'auth_rpc' | 'profiles_fallback' }> {
  const supabase = getSupabaseOrThrow()
  type AuthRow = { id: string; email: string | null; created_at: string }
  const authRows: AuthRow[] = []
  let offset = 0
  let firstRpc = true

  while (authRows.length < ADMIN_USERS_MAX) {
    const { data, error } = await supabase.rpc('admin_list_users', {
      p_limit: ADMIN_USERS_PAGE,
      p_offset: offset,
    })

    if (error) {
      if (firstRpc && authRows.length === 0) {
        return profilesFallback(limit)
      }
      throw new Error(error.message)
    }

    if (!Array.isArray(data)) {
      if (firstRpc && authRows.length === 0) {
        return profilesFallback(limit)
      }
      throw new Error('Nieprawidłowa odpowiedź z admin_list_users.')
    }

    firstRpc = false
    const page = data as AuthRow[]
    if (page.length === 0) break
    authRows.push(...page)
    if (page.length < ADMIN_USERS_PAGE) break
    offset += ADMIN_USERS_PAGE
  }

  const profileMap = await fetchProfilesByUserIds(authRows.map((u) => u.id))

  return {
    source: 'auth_rpc',
    rows: authRows.map((u) => {
      const pr = profileMap.get(u.id)
      const em = typeof u.email === 'string' ? u.email.trim() : ''
      return {
        id: u.id,
        email: em.length ? em : null,
        full_name: pr?.full_name ?? null,
        origin: pr?.origin ?? null,
        registered_at: u.created_at,
        marketing_consent: pr?.marketing_consent === true,
        marketing_consent_at: pr?.marketing_consent_at ?? null,
      }
    }),
  }
}

type TrialProfileRow = {
  id: string
  full_name: string | null
  origin: string | null
  created_at: string
  membership_paid_at: string | null
  marketing_consent: boolean | null
  marketing_consent_at: string | null
}

function mapTrialRow(
  p: TrialProfileRow,
  email: string | null,
): AdminTrialAccountRow {
  const em = email?.trim() ?? ''
  return {
    id: p.id,
    email: em.length ? em : null,
    full_name: p.full_name,
    origin: p.origin,
    created_at: p.created_at,
    membership_paid_at: p.membership_paid_at ?? null,
    marketing_consent: p.marketing_consent === true,
    marketing_consent_at: p.marketing_consent_at ?? null,
  }
}

async function trialAccountsProfilesFallback(
  limit: number,
): Promise<{ rows: AdminTrialAccountRow[]; source: 'profiles_fallback' }> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, full_name, origin, created_at, membership_paid_at, marketing_consent, marketing_consent_at',
    )
    .eq('account_access', 'places_preview')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)

  const profiles = (data ?? []) as TrialProfileRow[]
  const emailById = new Map<string, string>()
  try {
    const { rows } = await listAdminUsers(Math.min(limit, 5000))
    for (const u of rows) {
      if (u.email) emailById.set(u.id, u.email)
    }
  } catch {
    /* brak emaili w fallback */
  }

  return {
    source: 'profiles_fallback',
    rows: profiles.map((p) => mapTrialRow(p, emailById.get(p.id) ?? null)),
  }
}

/**
 * Konta z `account_access = places_preview` (darmowy podgląd listy miejsc).
 * Wymaga RPC `admin_list_trial_accounts`; przy braku — odczyt z `profiles` + emaile z Auth (jeśli dostępne).
 */
export async function listAdminTrialAccounts(limit = 2000): Promise<{
  rows: AdminTrialAccountRow[]
  source: 'rpc' | 'profiles_fallback'
}> {
  const supabase = getSupabaseOrThrow()
  const rows: AdminTrialAccountRow[] = []
  let offset = 0
  const pageSize = 500
  let firstRpc = true

  while (rows.length < limit) {
    const { data, error } = await supabase.rpc('admin_list_trial_accounts', {
      p_limit: Math.min(pageSize, limit - rows.length),
      p_offset: offset,
    })

    if (error) {
      if (firstRpc && rows.length === 0) {
        return trialAccountsProfilesFallback(limit)
      }
      throw new Error(error.message)
    }

    if (!Array.isArray(data)) {
      if (firstRpc && rows.length === 0) {
        return trialAccountsProfilesFallback(limit)
      }
      throw new Error('Nieprawidłowa odpowiedź z admin_list_trial_accounts.')
    }

    firstRpc = false
    const page = data as Array<{
      id: string
      email?: string | null
      full_name?: string | null
      origin?: string | null
      created_at: string
      membership_paid_at?: string | null
      marketing_consent?: boolean | null
      marketing_consent_at?: string | null
    }>

    if (page.length === 0) break

    for (const r of page) {
      const em = typeof r.email === 'string' ? r.email.trim() : ''
      rows.push({
        id: r.id,
        email: em.length ? em : null,
        full_name: r.full_name ?? null,
        origin: r.origin ?? null,
        created_at: r.created_at,
        membership_paid_at: r.membership_paid_at ?? null,
        marketing_consent: r.marketing_consent === true,
        marketing_consent_at: r.marketing_consent_at ?? null,
      })
    }

    if (page.length < pageSize) break
    offset += pageSize
  }

  return { rows, source: 'rpc' }
}

export async function countAdminTrialAccounts(): Promise<number | null> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_count_trial_accounts')
  if (error) return null
  if (typeof data === 'number' && Number.isFinite(data)) return Math.floor(data)
  return null
}

type FreePromoGrantRow = {
  id: string
  user_id: string
  email: string
  created_at: string
}

async function freePromoAccountsTableFallback(
  limit: number,
): Promise<{ rows: AdminFreePromoAccountRow[]; source: 'table_fallback' }> {
  const supabase = getSupabaseOrThrow()
  const { data: grants, error: grantsError } = await supabase
    .from('free_promo_grants')
    .select('id, user_id, email, created_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (grantsError) throw new Error(grantsError.message)

  const grantRows = (grants ?? []) as FreePromoGrantRow[]
  const userIds = grantRows.map((g) => g.user_id)
  const profileMap = await fetchProfilesByUserIds(userIds)

  return {
    source: 'table_fallback',
    rows: grantRows.map((g) => {
      const pr = profileMap.get(g.user_id)
      return {
        grant_id: g.id,
        user_id: g.user_id,
        email: g.email,
        full_name: pr?.full_name ?? null,
        origin: pr?.origin ?? null,
        granted_at: g.created_at,
        registered_at: pr?.created_at ?? null,
        marketing_consent: pr?.marketing_consent === true,
        marketing_consent_at: pr?.marketing_consent_at ?? null,
      }
    }),
  }
}

/**
 * Konta z programu /promocja (tabela free_promo_grants, max 10).
 * Wymaga RPC `admin_list_free_promo_grants`; przy braku — odczyt z tabeli (polityka admin).
 */
export async function listAdminFreePromoAccounts(limit = 50): Promise<{
  rows: AdminFreePromoAccountRow[]
  source: 'rpc' | 'table_fallback'
}> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_list_free_promo_grants', {
    p_limit: limit,
    p_offset: 0,
  })

  if (error) {
    return freePromoAccountsTableFallback(limit)
  }

  if (!Array.isArray(data)) {
    return freePromoAccountsTableFallback(limit)
  }

  const rows = data as Array<{
    grant_id: string
    user_id: string
    email: string
    full_name?: string | null
    origin?: string | null
    granted_at: string
    registered_at?: string | null
    marketing_consent?: boolean | null
    marketing_consent_at?: string | null
  }>

  return {
    source: 'rpc',
    rows: rows.map((r) => ({
      grant_id: r.grant_id,
      user_id: r.user_id,
      email: r.email,
      full_name: r.full_name ?? null,
      origin: r.origin ?? null,
      granted_at: r.granted_at,
      registered_at: r.registered_at ?? null,
      marketing_consent: r.marketing_consent === true,
      marketing_consent_at: r.marketing_consent_at ?? null,
    })),
  }
}

export async function countAdminFreePromoAccounts(): Promise<number | null> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_count_free_promo_grants')
  if (!error && typeof data === 'number' && Number.isFinite(data)) {
    return Math.floor(data)
  }

  const { count, error: countError } = await supabase
    .from('free_promo_grants')
    .select('id', { count: 'exact', head: true })
  if (countError) return null
  return count ?? null
}

export async function listComments(limit = 500): Promise<AdminPlaceComment[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('place_comments')
    .select('id, place_id, user_id, comment, created_at, updated_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)
  return (data ?? []) as AdminPlaceComment[]
}

export async function deleteComment(commentId: string): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('place_comments').delete().eq('id', commentId)
  if (error) throw new Error(error.message)
}

export async function listWaitlistSignups(limit = 500): Promise<AdminWaitlistSignup[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('waitlist_signups')
    .select('id, email, source, created_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)
  return (data ?? []) as AdminWaitlistSignup[]
}

export async function listUserJournalEntries(input: { userId: string; limit?: number }): Promise<AdminUserJournalEntry[]> {
  const supabase = getSupabaseOrThrow()
  const limit = input.limit ?? 200

  const { data, error } = await supabase.rpc('admin_list_user_journal_entries', {
    p_user_id: input.userId,
    p_limit: limit,
  })

  if (!error && Array.isArray(data)) {
    return (data as Array<{
      place_id: string
      visited_at: string | null
      verified_at: string | null
      verification_method: string | null
      photo_visibility: string | null
      has_photo: boolean
      has_note: boolean
      note_preview: string | null
      updated_at: string | null
      place_name: string | null
      place_mock_id: number | null
    }>).map((r) => ({
      place_id: r.place_id,
      visited_at: r.visited_at,
      verified_at: r.verified_at,
      verification_method: r.verification_method,
      note: r.note_preview,
      photo_url: null,
      has_photo: Boolean(r.has_photo),
      photo_visibility: r.photo_visibility === 'public' ? 'public' : 'private',
      updated_at: r.updated_at,
      place: {
        id: r.place_id,
        name: r.place_name ?? null,
        mock_id: r.place_mock_id ?? null,
      },
    }))
  }

  // Fallback bez ciężkiego photo_url (często base64). Flaga has_photo z osobnego lekkiego selecta.
  const [{ data: fallbackData, error: fallbackError }, { data: photoFlags, error: photoFlagsError }] =
    await Promise.all([
      supabase
        .from('user_place_progress')
        .select(
          'place_id, visited_at, verified_at, verification_method, photo_visibility, note, updated_at, places(id, name, mock_id)',
        )
        .eq('user_id', input.userId)
        .order('updated_at', { ascending: false })
        .limit(limit),
      supabase
        .from('user_place_progress')
        .select('place_id')
        .eq('user_id', input.userId)
        .not('photo_url', 'is', null)
        .neq('photo_url', ''),
    ])

  if (fallbackError) throw new Error(fallbackError.message)
  if (photoFlagsError) throw new Error(photoFlagsError.message)

  const placesWithPhoto = new Set(
    ((photoFlags ?? []) as Array<{ place_id: string }>).map((r) => r.place_id),
  )

  const rows = ((fallbackData ?? []) as unknown) as Array<{
    place_id: string
    visited_at: string | null
    verified_at: string | null
    verification_method: string | null
    photo_visibility: string | null
    note: string | null
    updated_at: string | null
    places?: { id: string; name: string | null; mock_id: number | null } | Array<{ id: string; name: string | null; mock_id: number | null }> | null
  }>

  return rows.map((r) => {
    const place = Array.isArray(r.places) ? (r.places[0] ?? null) : (r.places ?? null)
    const note = r.note?.trim() || null
    return {
      place_id: r.place_id,
      visited_at: r.visited_at,
      verified_at: r.verified_at,
      verification_method: r.verification_method,
      note: note ? note.slice(0, 400) : null,
      photo_url: null,
      has_photo: placesWithPhoto.has(r.place_id),
      photo_visibility: r.photo_visibility === 'public' ? 'public' : 'private',
      updated_at: r.updated_at,
      place: place ? { id: place.id, name: place.name ?? null, mock_id: place.mock_id ?? null } : null,
    } satisfies AdminUserJournalEntry
  })
}

export async function getUserJournalEntryPhoto(input: { userId: string; placeId: string }): Promise<string | null> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_get_user_journal_entry_photo', {
    p_user_id: input.userId,
    p_place_id: input.placeId,
  })
  if (!error) {
    return typeof data === 'string' && data.trim() ? data.trim() : null
  }

  const { data: row, error: fallbackError } = await supabase
    .from('user_place_progress')
    .select('photo_url')
    .eq('user_id', input.userId)
    .eq('place_id', input.placeId)
    .maybeSingle()
  if (fallbackError) throw new Error(fallbackError.message)
  const url = (row as { photo_url: string | null } | null)?.photo_url
  return url?.trim() ? url.trim() : null
}

export async function deleteUserJournalEntry(input: { userId: string; placeId: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('user_place_progress').delete().eq('user_id', input.userId).eq('place_id', input.placeId)
  if (error) throw new Error(error.message)
}

export async function listUserJournalPhotos(input: { userId: string; limit?: number }): Promise<AdminUserJournalPhoto[]> {
  const supabase = getSupabaseOrThrow()
  const limit = input.limit ?? 200

  const { data, error } = await supabase.rpc('admin_list_user_journal_photos', {
    p_user_id: input.userId,
    p_limit: limit,
  })

  if (!error && Array.isArray(data)) {
    return (data as Array<{
      id: string
      user_id: string
      place_id: string
      url: string | null
      is_storage_url: boolean
      visibility: string | null
      created_at: string
      place_name: string | null
      place_mock_id: number | null
    }>).map((r) => ({
      id: r.id,
      user_id: r.user_id,
      place_id: r.place_id,
      url: r.url?.trim() ?? '',
      is_storage_url: Boolean(r.is_storage_url),
      visibility: r.visibility === 'public' ? 'public' : 'private',
      created_at: r.created_at,
      place: {
        id: r.place_id,
        name: r.place_name ?? null,
        mock_id: r.place_mock_id ?? null,
      },
    }))
  }

  const { data: fallbackData, error: fallbackError } = await supabase
    .from('user_place_photos')
    .select('id, user_id, place_id, url, visibility, created_at, places(id, name, mock_id)')
    .eq('user_id', input.userId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (fallbackError) throw new Error(fallbackError.message)

  const rows = ((fallbackData ?? []) as unknown) as Array<{
    id: string
    user_id: string
    place_id: string
    url: string
    visibility: string | null
    created_at: string
    places?: { id: string; name: string | null; mock_id: number | null } | Array<{ id: string; name: string | null; mock_id: number | null }> | null
  }>

  return rows.map((r) => {
    const place = Array.isArray(r.places) ? (r.places[0] ?? null) : (r.places ?? null)
    const url = r.url?.trim() ?? ''
    return {
      id: r.id,
      user_id: r.user_id,
      place_id: r.place_id,
      url: url.startsWith('http') ? url : '',
      is_storage_url: url.startsWith('http'),
      visibility: r.visibility === 'public' ? 'public' : 'private',
      created_at: r.created_at,
      place: place ? { id: place.id, name: place.name ?? null, mock_id: place.mock_id ?? null } : null,
    } satisfies AdminUserJournalPhoto
  })
}

export async function getUserJournalPhotoUrl(photoId: string): Promise<string | null> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('admin_get_user_journal_photo_url', {
    p_photo_id: photoId,
  })
  if (!error) {
    return typeof data === 'string' && data.trim() ? data.trim() : null
  }

  const { data: row, error: fallbackError } = await supabase
    .from('user_place_photos')
    .select('url')
    .eq('id', photoId)
    .maybeSingle()
  if (fallbackError) throw new Error(fallbackError.message)
  const url = (row as { url: string | null } | null)?.url
  return url?.trim() ? url.trim() : null
}

export async function deleteUserJournalPhoto(input: { userId: string; photoId: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('user_place_photos').delete().eq('id', input.photoId).eq('user_id', input.userId)
  if (error) throw new Error(error.message)
}

export type AdminStatBucket = {
  label: string
  count: number
}

export type AdminParticipantStats = {
  totalUsers: number
  profilesCount: number
  withOrigin: number
  withHeardAbout: number
  uniqueOrigins: number
  origins: AdminStatBucket[]
  heardAbout: AdminStatBucket[]
}

const STATS_PROFILES_LIMIT = 10_000

function normalizeOriginKey(raw: string): string {
  return raw.trim().toLocaleLowerCase('pl-PL')
}

function groupCount(rows: string[], emptyLabel = 'Nie podano'): AdminStatBucket[] {
  const map = new Map<string, { label: string; count: number }>()
  for (const raw of rows) {
    const trimmed = raw.trim()
    if (!trimmed) {
      const prev = map.get('') ?? { label: emptyLabel, count: 0 }
      map.set('', { label: emptyLabel, count: prev.count + 1 })
      continue
    }
    const key = normalizeOriginKey(trimmed)
    const prev = map.get(key) ?? { label: trimmed, count: 0 }
    map.set(key, { label: prev.label, count: prev.count + 1 })
  }
  return [...map.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'pl'))
}

/** Liczba kont Auth przez admin_list_users (bez registered_user_count w bazie). */
async function countAdminAuthUsers(): Promise<number> {
  const supabase = getSupabaseOrThrow()
  let total = 0
  let offset = 0

  while (total < ADMIN_USERS_MAX) {
    const { data, error } = await supabase.rpc('admin_list_users', {
      p_limit: ADMIN_USERS_PAGE,
      p_offset: offset,
    })
    if (error) {
      const { count, error: countError } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
      if (countError) throw new Error(countError.message)
      return count ?? 0
    }
    if (!Array.isArray(data)) throw new Error('Nieprawidłowa odpowiedź z admin_list_users.')
    if (data.length === 0) break
    total += data.length
    if (data.length < ADMIN_USERS_PAGE) break
    offset += ADMIN_USERS_PAGE
  }

  return total
}

/** Statystyki uczestników: liczba kont, pochodzenie, kanał pozyskania. */
export async function getAdminParticipantStats(): Promise<AdminParticipantStats> {
  const supabase = getSupabaseOrThrow()

  const [totalUsers, profilesRes] = await Promise.all([
    countAdminAuthUsers(),
    supabase.from('profiles').select('origin, heard_about_us').limit(STATS_PROFILES_LIMIT),
  ])

  if (profilesRes.error) throw new Error(profilesRes.error.message)

  const profiles = (profilesRes.data ?? []) as Array<{ origin: string | null; heard_about_us: string | null }>
  const originsRaw = profiles.map((p) => p.origin ?? '')
  const heardRaw = profiles.map((p) => p.heard_about_us ?? '')

  const withOrigin = originsRaw.filter((o) => o.trim().length > 0).length
  const withHeardAbout = heardRaw.filter((h) => h.trim().length > 0).length
  const uniqueOrigins = new Set(originsRaw.map((o) => normalizeOriginKey(o)).filter(Boolean)).size

  return {
    totalUsers,
    profilesCount: profiles.length,
    withOrigin,
    withHeardAbout,
    uniqueOrigins,
    origins: groupCount(originsRaw),
    heardAbout: groupCount(heardRaw),
  }
}

